import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import * as catCodes from '@shared-constants/cat-codes-constants';
import { CatsRecordTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { limitDecimalDisplay, trimDecimalZeros, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IEmployeeActivity, IForemanCatsProperties } from '@shared-interfaces/api';
import { IForemanEmployee } from '@shared-interfaces/api';

import { ICreateEmployeeBillable, IModifyCatsRecordHours } from '../../models/cats-outbound.model';
import { DatahubService } from '../../services/datahub.service';
import { IRowColumn, IRowNameKey } from './employee.model';

export class Employee {
  display: string;
  rows: {
    st: IRowColumn;
    ot: IRowColumn;
    dt: IRowColumn;
  };
  originalWorkDateTotal: number;
  originalWeDateTotal: number;
  workDateTotal = 0;
  weDateTotal = 0;
  differential = 0;
  catsRecords: IForemanCatsProperties[] = [];
  EmpToActivity: { results: IEmployeeActivity[] } = { results: [] };
  LastName = '';
  FirstName = '';
  CostCenter = '';
  CompanyCode = '';
  EmpId = '';
  listener: Subscription[] = [];
  DAWeek = '';
  CCWeek = '';
  InvalidDates = '';
  isPending = false;

  constructor(
    public employeeRecord: IForemanEmployee,
    public datahub: DatahubService,
  ) {
    syncObjectProperties(employeeRecord, this, false);
    const day = this.datahub.date.getDayOfWeek();
    this.catsRecords = employeeRecord.records;
    this.display = employeeRecord.display;
    this.rows = this.catsToRows(day);
    this.originalWeDateTotal = Number(employeeRecord.weeklyTotal);
    this.originalWorkDateTotal = Number(employeeRecord.dailyTotal);
    this.calculateTotals();
  }

  calculateTotals() {
    const timesheetDailyTotal =
      Number(this.rows.st.hours.value) + Number(this.rows.ot.hours.value) + Number(this.rows.dt.hours.value);
    const difference = timesheetDailyTotal - this.differential;
    this.workDateTotal = this.originalWorkDateTotal + difference;
    this.weDateTotal = this.originalWeDateTotal + difference;
  }

  catsToRows(day: number) {
    const defaultStraightActivityCode = trimLeadingZeros(this.parsePseudoArray(this.DAWeek, day));
    const defaultOverActivityCode = defaultStraightActivityCode.replace('2', '5');
    const defaultDoubleActivityCode = defaultStraightActivityCode.replace('2', '6');
    const st = this.catsRecords.filter((x) => x.AbsAttType === catCodes.STRAIGHT_TIME_ATTENDANCE_CODE);
    const ot = this.catsRecords.filter(
      (x) =>
        x.AbsAttType === catCodes.OVERTIME_ATTENDANCE_CODE || x.AbsAttType === catCodes.SPECIAL_ACCRUAL_OVERTIME_ATTENDANCE_CODE,
    );
    const dt = this.catsRecords.filter(
      (x) =>
        x.AbsAttType === catCodes.DOUBLE_TIME_ATTENDANCE_CODE ||
        x.AbsAttType === catCodes.SPECIAL_ACCRUAL_DOUBLE_TIME_ATTENDANCE_CODE,
    );
    return {
      st: this.resolveArrayToRow(st, catCodes.STRAIGHT_TIME_ATTENDANCE_CODE, defaultStraightActivityCode, 'st'),
      ot: this.resolveArrayToRow(ot, catCodes.OVERTIME_ATTENDANCE_CODE, defaultOverActivityCode, 'ot'),
      dt: this.resolveArrayToRow(dt, catCodes.DOUBLE_TIME_ATTENDANCE_CODE, defaultDoubleActivityCode, 'dt'),
    };
  }

  getActivityStatus(code: string): string {
    const day = this.datahub.date.selectedDate.value.day();
    if (this.InvalidDates.includes(day.toString())) return 'notActiveForDayError';
    const found = this.EmpToActivity.results.find((x: IEmployeeActivity) => x.ActivityType === code);
    if (!found) return 'invalidCostingError';
    if (found.InvalidDates.includes(day.toString())) return 'invalidCostingError';
    return 'ok';
  }

  private setControlValueAndListen(value: string, type: IRowNameKey, isDisabled: boolean, error?: string) {
    this.differential += Number(value);
    const control = new FormControl<string>({ value: value, disabled: isDisabled }, { nonNullable: true });
    const errorObj: Record<string, boolean> = {}; //errors have to be objects for reactive controls
    if (error) {
      errorObj[error] = true;
      control.setErrors(errorObj);
    }
    const listener = control.valueChanges.subscribe((valChange) => {
      this.handleValueChanges(control, type, valChange);
    });
    this.listener.push(listener);
    return control;
  }

  private handleValueChanges(control: FormControl<string>, type: IRowNameKey, value: string) {
    if (!this.rows[type].isDisabled) this.rows[type].shouldUpload = true; //flag this box for upload
    if (!this.rows[type].isDisabled) this.rows[type].isModified = true; //flag this box as modified
    if (!Number(value)) {
      if (this.rows[type].isNew) this.rows[type].shouldUpload = false; //if its a new record, lets not upload a bunch of 0 value records into cats for no reason.
    }
    this.calculateTotals();
    this.checkForTemporalOverflowErrors(control); //check to see if the daily total is over 24 hours.
  }

  private checkForTemporalOverflowErrors(control: FormControl<string>): void {
    if (this.workDateTotal <= 24 && control.hasError('temporalOverflowError')) {
      control.setErrors({ temporalOverflowError: null }); //remove the error
    } else if (this.workDateTotal > 24 && control.errors == null) {
      //Don't overwrite an existing error
      control.setErrors({ temporalOverflowError: true });
    }
  }

  // eslint-disable-next-line complexity -- This needs to be simplified to be less complex, but out of scope for now
  resolveArrayToRow(
    catsArray: IForemanCatsProperties[],
    attendanceType: string,
    activityCode: string,
    type: IRowNameKey,
  ): IRowColumn {
    const error: string = this.getActivityStatus(activityCode);
    const isInvalid = error !== 'ok';
    if (catsArray.length <= 0) {
      return {
        catsRecords: [],
        attendanceType: attendanceType,
        activityCode: activityCode,
        counter: '',
        hours: this.setControlValueAndListen('', type, isInvalid, this.determineError(error)),
        isDisabled: false,
        isComplex: false,
        shouldUpload: false,
        isNew: true,
      };
    } else if (catsArray.length === 1) {
      const record = catsArray[0];
      const hours = Number(record?.Catshours ?? '');
      const status = Number(record?.Status ?? '');
      const isApproved = status > CatsStatusCodeEnum.ReadyForApproval;
      return {
        catsRecords: catsArray,
        attendanceType: attendanceType,
        activityCode: activityCode,
        counter: record?.Counter ?? '',
        hours: this.setControlValueAndListen(hours.toString(), type, isApproved || isInvalid, this.determineError(error, status)),
        isDisabled: isApproved,
        isComplex: false,
        shouldUpload: false,
        isNew: false,
      };
    } else {
      //If more than one...
      let hours = 0;
      catsArray.forEach((element) => (hours += Number(element.Catshours)));
      return {
        catsRecords: catsArray,
        hours: this.setControlValueAndListen(hours.toString(), type, true, 'multipleRecordsForDayError'),
        attendanceType: attendanceType,
        activityCode: activityCode,
        isNew: false,
        isComplex: true,
        isDisabled: true,
        shouldUpload: false,
      };
    }
  }

  public removeSelf(employeeArray: any[], shouldUnsubscribe?: boolean): void {
    this.catsRecords = [];
    const index = employeeArray.findIndex((employee) => employee.EmpId === this.EmpId);
    if (index < 0) return;
    if (shouldUnsubscribe) {
      this.listener.map((x) => {
        x.unsubscribe();
      });
    }
    employeeArray.splice(index, 1);
  }

  public deleteSelf(employeeArray: any[]) {
    this.datahub.timesheet.flagForDelete(this);
    this.removeSelf(employeeArray);
  }

  public findCounters(): string[] {
    const counters: string[] = [];
    if (typeof this.rows.st.counter === 'string' && this.rows.st.counter !== '') counters.push(this.rows.st.counter);
    if (typeof this.rows.dt.counter === 'string' && this.rows.dt.counter !== '') counters.push(this.rows.dt.counter);
    if (typeof this.rows.ot.counter === 'string' && this.rows.ot.counter !== '') counters.push(this.rows.ot.counter);
    return counters;
  }

  public addSelf(employeeArray: any[]) {
    employeeArray.push(this);
  }

  public getCatRecords(): Array<IModifyCatsRecordHours | ICreateEmployeeBillable> {
    const records: Array<IModifyCatsRecordHours | ICreateEmployeeBillable> = [];
    Object.keys(this.rows).forEach((key) => {
      const row = key as IRowNameKey;
      if (this.rows[row].shouldUpload) {
        if (this.rows[row].isNew) {
          records.push(this.makeNewEmployeeCatsRecord(this.rows[row])); //create case
        } else {
          records.push(this.formatModify(this.rows[row])); //modify case
        }
      }
    });

    return records;
  }

  private determineError(error?: string, status?: number): string | undefined {
    if (Number(status) > CatsStatusCodeEnum.ReadyForApproval) return 'alreadyApprovedError';
    if (error === 'ok') return undefined;
    return error;
  }

  public getGraphableValues() {
    return [
      this.display,
      ...this.getHours(),
      trimDecimalZeros(limitDecimalDisplay(this.workDateTotal)),
      trimDecimalZeros(limitDecimalDisplay(this.weDateTotal)),
    ];
  }

  private getHours() {
    return Object.keys(this.rows).map((key) => this.rows[key as IRowNameKey].hours.value);
  }

  private makeNewEmployeeCatsRecord(row: IRowColumn): ICreateEmployeeBillable {
    return {
      ZempCreatedBy: this.datahub.timesheet.findTokenEmployee(),
      Employeenumber: this.EmpId,
      Catshours: row.hours.value.trim() === '' ? '0' : row.hours.value,
      Workdate: this.datahub.date.getOdataString(),
      Acttype: row.activityCode,
      RecOrder: this.datahub.contract.getServiceOrder(),
      AbsAttType: row.attendanceType,
      SendCctr: this.CostCenter,
      Zrecordtype: CatsRecordTypeEnum.EmployeeBillable,
      Extdocumentno: this.datahub.extDocument.Documentno,
      Zmatnr: this.datahub.contract.getEmployeeDefault(row.activityCode, this.EmpId),
    };
  }

  private formatModify(row: IRowColumn): IModifyCatsRecordHours {
    if (row.counter === undefined) throw Error('attempted to modify a record that did not exist!');
    return {
      Counter: row.counter,
      Catshours: row.hours.value.trim() === '' ? '0' : row.hours.value,
      Catshours_X: true,
    };
  }

  private parsePseudoArray(stringArray: string, index: number): string {
    return stringArray.split(',')[index] ?? '';
  }
}
